import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { Trip } from '../../classes/trip';

import { LocalStorageService } from 'src/app/core/services/local-storage.service';

enum LocalStorageKeys {
  TripDetails = 'tripDetails',
  TripHistoryStack = 'tripHistoryStack',
  TripHistoryIndex = 'tripHistoryIndex',
}

@Injectable({
  providedIn: 'root',
})
export class TripWizardStateService {
  onNewCreateTrip$: Subject<void> = new Subject<void>();
  #localStorageService: LocalStorageService = inject(LocalStorageService);
  #currentTripState: BehaviorSubject<Trip> = new BehaviorSubject<Trip>(
    this.restoreTripStateFromLocalStorage(),
  );

  #historyStack: string[] = [];
  #currentStackIndex: number = -1;
  #byUserInteraction: boolean = false;

  constructor() {
    this.initHistoryStackByLocalStorage();
  }

  public get currentTripStateValue(): Trip {
    return this.#currentTripState.value;
  }

  public get historyStack(): string[] {
    return this.#historyStack;
  }

  public get currentStackIndex(): number {
    return this.#currentStackIndex;
  }

  public get isUndoDisabled(): boolean {
    if (!this.historyStack.length) return true;
    return this.currentStackIndex <= 0;
  }

  public get isRedoDisabled(): boolean {
    if (!this.historyStack.length) return true;
    return this.currentStackIndex === this.historyStack.length - 1;
  }

  public get currentTripState(): Observable<Trip> {
    return this.#currentTripState.asObservable();
  }

  public set currentTripState(modifiedTrip: Trip) {
    this.#currentTripState.next(modifiedTrip);
    this.storeTripStateToLocalStorage(modifiedTrip);
  }

  public set byUserInteraction(ev: boolean) {
    this.#byUserInteraction = ev;
  }

  get currentTripPaxString(): string {
    const {
      rooms: { rooms, adults },
    } = this.currentTripStateValue;
    return `${rooms}|${adults}`;
  }

  get currentTripPaxChildrenAgesString(): string {
    const {
      rooms: { children, ages },
    } = this.currentTripStateValue;
    if (children === 0) return '';
    return ages.join(',');
  }

  get savedTripName(): string {
    const { name, startingLocationId, route } = this.currentTripStateValue;
    if (!name) return '';
    if (route.length) {
      const firstRoute = route[0];
      if (startingLocationId.toString() !== firstRoute.id.toString()) {
        return '';
      }
      return name;
    }
    return '';
  }

  public updateHistoryStack(): void {
    if (this.#byUserInteraction) {
      if (
        this.#historyStack.length &&
        this.#historyStack.length - 1 !== this.#currentStackIndex
      ) {
        this.#historyStack.length = this.#currentStackIndex + 1;
      }
      this.#historyStack.push(JSON.stringify(this.currentTripStateValue));
      this.#currentStackIndex = this.#historyStack.length - 1;
      this.storeHistoryStackToLocalStorage();
      this.byUserInteraction = false;
    }
  }

  public undo(): void {
    if (this.#currentStackIndex > 0) {
      this.#currentStackIndex--;
      const tripStateByHistory = this.#historyStack[this.#currentStackIndex];
      if (tripStateByHistory) {
        this.currentTripState = new Trip(JSON.parse(tripStateByHistory));
        this.storeHistoryStackToLocalStorage();
      }
    }
  }

  public redo(): void {
    this.#currentStackIndex++;
    const tripStateByHistory = this.#historyStack[this.#currentStackIndex];
    if (tripStateByHistory) {
      this.currentTripState = new Trip(JSON.parse(tripStateByHistory));
      this.storeHistoryStackToLocalStorage();
    }
  }

  public resetHistoryStack(): void {
    this.#historyStack = [];
    this.#currentStackIndex = -1;
    this.storeHistoryStackToLocalStorage();
  }

  private restoreTripStateFromLocalStorage(): Trip {
    const trip = this.#localStorageService.getLocalStorage(
      LocalStorageKeys.TripDetails,
      new Trip(),
    );
    if (!this.isValidTravelersObject(trip.rooms)) {
      trip.rooms = { adults: 2, ages: [], children: 0, rooms: 1 };
    }
    return new Trip(trip);
  }

  private storeTripStateToLocalStorage(trip: Trip): void {
    this.#localStorageService.setLocalStorage(
      LocalStorageKeys.TripDetails,
      trip,
    );
  }

  private storeHistoryStackToLocalStorage(): void {
    this.#localStorageService.setLocalStorage(
      LocalStorageKeys.TripHistoryStack,
      this.historyStack,
    );
    this.#localStorageService.setLocalStorage(
      LocalStorageKeys.TripHistoryIndex,
      this.currentStackIndex,
    );
  }

  private initHistoryStackByLocalStorage(): void {
    this.#historyStack = this.#localStorageService.getLocalStorage(
      LocalStorageKeys.TripHistoryStack,
      this.#historyStack,
    );
    this.#currentStackIndex = this.#localStorageService.getLocalStorage(
      LocalStorageKeys.TripHistoryIndex,
      this.#currentStackIndex,
    );
  }

  private isValidTravelersObject(travelers: {
    adults: number;
    ages: number[];
    children: number;
    rooms: number;
  }): boolean {
    if (!travelers || !Object.keys(travelers).length) {
      return false;
    }

    const { adults, children, ages, rooms } = travelers;

    return (
      Number.isInteger(adults) &&
      adults >= 1 &&
      Number.isInteger(children) &&
      children >= 0 &&
      Array.isArray(ages) &&
      Number.isInteger(rooms) &&
      rooms >= 1
    );
  }
}
